import { Component } from 'react';
import HoldingLayout from './HoldingLayout';

const FEATURES = [
  {
    title: 'Conversation Management',
    titleAr: 'إدارة المحادثات',
    description: 'View and manage all customer conversations in one place. Track performance metrics, monitor response times, and follow up on every lead interaction across channels.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6h24v16H16l-6 4v-4H4V6z" /><line x1="10" y1="12" x2="22" y2="12" opacity="0.5" /><line x1="10" y1="16" x2="18" y2="16" opacity="0.3" />
      </svg>
    ),
  },
  {
    title: 'Real-Time Analytics',
    titleAr: 'البيانات اللحظية',
    description: 'Live dashboards tracking total conversations, active users, average response time, cost savings, agent performance scores, and lead quality — all updating in real time.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 24 10 16 16 20 22 10 28 14" /><line x1="4" y1="28" x2="28" y2="28" /><line x1="4" y1="4" x2="4" y2="28" />
      </svg>
    ),
  },
  {
    title: 'Multi-Platform Ad Campaigns',
    titleAr: 'الحملات الإعلانية',
    description: 'Connect and manage advertising campaigns across Snapchat, TikTok, Instagram, and Google Ads from a single dashboard. Monitor spend, performance, and ROI across all platforms.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="12" height="12" rx="3" /><rect x="17" y="3" width="12" height="12" rx="3" /><rect x="3" y="17" width="12" height="12" rx="3" /><rect x="17" y="17" width="12" height="12" rx="3" opacity="0.4" />
      </svg>
    ),
  },
  {
    title: 'Sales & Inventory Management',
    titleAr: 'المبيعات والمخزون',
    description: 'Track real estate projects, units, and inventory in real time. See total units, sold, reserved, and available counts. Monitor project revenue, average unit prices, and sales velocity with visual charts.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="6" width="20" height="20" rx="3" /><line x1="6" y1="16" x2="26" y2="16" /><line x1="16" y1="6" x2="16" y2="26" /><circle cx="11" cy="11" r="2" opacity="0.4" /><circle cx="21" cy="21" r="2" opacity="0.4" />
      </svg>
    ),
  },
  {
    title: 'CRM & Lead Tracking',
    titleAr: 'إدارة العلاقات',
    description: 'Full customer relationship management with lead scoring, deal pipelines, assigned sales reps, and priority tracking. Never lose a lead — every conversation and interaction is logged.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="10" r="4" /><circle cx="22" cy="12" r="3" /><path d="M4 26c0-5 4-8 8-8s8 3 8 8" /><path d="M20 26c0-3.5 2-6 5-6s5 2.5 5 6" opacity="0.5" />
      </svg>
    ),
  },
  {
    title: 'Sales Rep Management',
    titleAr: 'مندوبي المبيعات',
    description: 'Assign leads to sales reps, track their performance, and coordinate tasks across your team. See who is handling what and ensure every opportunity gets the right attention.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="16" cy="10" r="5" /><path d="M6 28c0-6 4.5-10 10-10s10 4 10 10" /><path d="M22 8l4-4M22 4l4 4" opacity="0.4" />
      </svg>
    ),
  },
];

const PLATFORMS = [
  { name: 'سناب شات', nameEn: 'Snapchat', desc: 'Campaign management & analytics' },
  { name: 'انستجرام', nameEn: 'Instagram', desc: 'Ad management & reporting' },
  { name: 'تيك توك', nameEn: 'TikTok', desc: 'Campaign management & targeting' },
  { name: 'إعلانات جوجل', nameEn: 'Google Ads', desc: 'Campaign management & reporting' },
];

const SCREENSHOTS = [
  { label: 'Dashboard & Conversations', labelAr: 'لوحة التحكم والمحادثات', src: '/images/screenshot-dashboard-main.png' },
  { label: 'Snapchat Campaign Management', labelAr: 'إدارة حملات سناب شات', src: '/images/screenshot-snapchat.png' },
  { label: 'Campaign Analytics', labelAr: 'تحليلات الحملات', src: '/images/screenshot-campaigns.png' },
  { label: 'Sales & Inventory', labelAr: 'المبيعات والمخزون', src: '/images/screenshot-inventory.png' },
];

class ProductSquareMeter extends Component {
  constructor(props) {
    super(props);
    this.state = { lightboxSrc: null, lightboxLabel: null };
  }

  openLightbox = (src, label) => {
    this.setState({ lightboxSrc: src, lightboxLabel: label });
    document.body.style.overflow = 'hidden';
  };

  closeLightbox = () => {
    this.setState({ lightboxSrc: null, lightboxLabel: null });
    document.body.style.overflow = '';
  };

  render() {
    const { lightboxSrc, lightboxLabel } = this.state;

    return (
      <HoldingLayout>
        {/* Hero */}
        <section className="h-page-hero">
          <div className="h-hero-bg-pattern" aria-hidden="true" />
          <div className="h-section-inner">
            <a href="/" className="h-back-link">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 8H3M3 8L7 4M3 8L7 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Back to Home
            </a>
            <div className="h-page-hero-content">
              <span className="h-section-tag">Product</span>
              <h1 className="h-page-hero-title">Square Meter</h1>
              <p className="h-page-hero-arabic">متر مربع — لوحة التحكم الشاملة</p>
              <p className="h-page-hero-desc">
                The all-in-one real estate operations platform. Manage customer conversations,
                run ad campaigns across Snapchat, TikTok, Instagram, and Google Ads,
                track sales and inventory, and coordinate your team — from a single Arabic-first dashboard.
              </p>
              <div className="h-page-hero-actions">
                <a href="/contact" className="h-btn-primary">
                  Contact Us
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </a>
                <span className="h-page-hero-status">
                  <span className="h-status-dot" />
                  Live
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Connected Platforms */}
        <section className="h-page-features">
          <div className="h-section-inner">
            <div className="h-section-header">
              <span className="h-section-tag">Connected Platforms</span>
              <h2 className="h-section-title">All your ad platforms, one dashboard</h2>
              <p className="h-section-subtitle">
                Connect your advertising accounts and manage campaigns across every major platform without switching tools.
              </p>
            </div>
            <div className="h-platforms-grid">
              {PLATFORMS.map((p, i) => (
                <div key={i} className="h-platform-card">
                  <span className="h-platform-ar">{p.name}</span>
                  <span className="h-platform-en">{p.nameEn}</span>
                  <span className="h-platform-desc">{p.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="h-page-screenshots">
          <div className="h-section-inner">
            <div className="h-section-header">
              <span className="h-section-tag">Features</span>
              <h2 className="h-section-title">Everything real estate teams need</h2>
              <p className="h-section-subtitle">
                From lead conversations to unit inventory — Square Meter covers the full sales pipeline.
              </p>
            </div>
            <div className="h-features-grid">
              {FEATURES.map((feature, i) => (
                <div key={i} className="h-feature-card">
                  <div className="h-feature-icon">{feature.icon}</div>
                  <h3 className="h-feature-title">{feature.title}</h3>
                  {feature.titleAr && <span className="h-feature-title-ar">{feature.titleAr}</span>}
                  <p className="h-feature-desc">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Screenshots */}
        <section className="h-page-features">
          <div className="h-section-inner">
            <div className="h-section-header">
              <span className="h-section-tag">Platform Preview</span>
              <h2 className="h-section-title">See it in action</h2>
            </div>
            <div className="h-screenshots-grid">
              {SCREENSHOTS.map((shot, i) => (
                <div
                  key={i}
                  className="h-screenshot-card h-screenshot-clickable"
                  onClick={() => this.openLightbox(shot.src, `${shot.labelAr} — ${shot.label}`)}
                >
                  <img
                    src={shot.src}
                    alt={shot.label}
                    className="h-screenshot-img h-screenshot-thumb"
                    loading="lazy"
                  />
                  <p className="h-screenshot-label">{shot.labelAr} — {shot.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="h-page-cta">
          <div className="h-section-inner">
            <div className="h-cta-box">
              <h2 className="h-cta-title">Ready to streamline your real estate operations?</h2>
              <p className="h-cta-desc">
                Join real estate teams across Saudi Arabia who manage their campaigns, conversations, and sales with Square Meter.
              </p>
              <a href="/contact" className="h-btn-primary h-btn-lg">
                Contact Us
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
            </div>
          </div>
        </section>
        {/* Lightbox */}
        {lightboxSrc && (
          <div className="h-lightbox-overlay" onClick={this.closeLightbox}>
            <div className="h-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button className="h-lightbox-close" onClick={this.closeLightbox}>&times;</button>
              <img src={lightboxSrc} alt={lightboxLabel} className="h-lightbox-img" />
              <p className="h-lightbox-label">{lightboxLabel}</p>
            </div>
          </div>
        )}
      </HoldingLayout>
    );
  }
}

export default ProductSquareMeter;
